import {
  AsyncCreatableSelect,
  Props,
  Select,
  MultiValue,
  ActionMeta,
} from 'chakra-react-select';
import { AutocompleteItem } from '@habity/types/autocomplete';
import { useState } from 'react';
import { Text, HStack } from '@chakra-ui/react';
import { SearchTab } from '@habity/types';

type SelectValue = AutocompleteItem[];

export interface CustomSelectProps
  extends Omit<
    Props<AutocompleteItem, true>,
    'loadOptions' | 'value' | 'onChange' | 'isMulti'
  > {
  inputType: 'async' | 'creatable' | 'async-creatable' | 'select';
  ignoreSearch?: boolean;
  tab?: SearchTab;
  loadOptions?: (
    inputValue: string,
    callback: (options: AutocompleteItem[]) => void
  ) => void;
  value?: SelectValue;
  onChange?: (newValue: SelectValue) => void;
}

export const CustomSelect = (props: CustomSelectProps) => {
  const {
    inputType,
    ignoreSearch,
    tab,
    loadOptions,
    value,
    onChange,
    ...rest
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (
    newValue: MultiValue<AutocompleteItem>,
    actionMeta: ActionMeta<AutocompleteItem>
  ) => {
    if (onChange) {
      onChange([...newValue]);
    }
  };

  const getOptionDescription = (option: AutocompleteItem) => {
    switch (option.type) {
      case 'municipality':
        return 'Sverige';
      case 'area':
        return option.municipality ? option.municipality.name : '';
      case 'broker':
        return option.municipality?.name || '';
      case 'street':
        return option.city || '';
      case 'project':
        return option.city || '';
      case 'street_shortcut':
        return 'Sök gatuadress';
      default:
        return '';
    }
  };

  const getOptionTypeLabel = (option: AutocompleteItem) => {
    switch (option.type) {
      case 'municipality':
        return 'Kommun';
      case 'area':
        return 'Område';
      case 'broker':
        return option.title || 'Mäklare';
      case 'street':
        return 'Gata';
      case 'project':
        return option.is_new_construction ? 'Nyproduktion' : 'Bostadsprojekt';
      default:
        return '';
    }
  };

  const loadOptionsPromise = async (inputValue: string) => {
    setIsLoading(true);
    return new Promise<AutocompleteItem[]>((resolve) => {
      if (loadOptions) {
        loadOptions(inputValue, (options) => {
          setIsLoading(false);
          // Return flat list - results are already sorted by relevance from the API
          resolve(options);
        });
      } else {
        setIsLoading(false);
        resolve([]);
      }
    });
  };

  const selectProps = {
    ...rest,
    isLoading,
    isClearable: true,
    isSearchable: true,
    isMulti: true as const,
    // Filter out options with empty names
    filterOption: (option: any) => {
      return option.data && option.data.name && option.data.name.trim() !== '';
    },
    // Prevent showing "No options" message when input is empty
    noOptionsMessage: ({ inputValue }: { inputValue: string }) => {
      return inputValue.trim() === '' ? null : 'Inga träffar';
    },
    getOptionValue: (option: AutocompleteItem) => String(option.id),
    getOptionLabel: (option: AutocompleteItem) => option.name,
    formatOptionLabel: (
      option: AutocompleteItem,
      { context }: { context: string }
    ) => (
      <HStack
        spacing="2"
        alignItems="baseline"
        justifyContent="space-between"
        width="100%"
      >
        <HStack spacing="2" alignItems="baseline">
          {/* Name */}
          <Text fontWeight="semibold" size={{ mobile: 'sm', desktop: 'md' }}>
            {option.name}
          </Text>

          {/* Description */}
          {context === 'menu' && option.type && (
            <Text
              size={{ mobile: 'xs', desktop: 'sm' }}
              color="blackAlpha.700"
              fontWeight="light"
            >
              {getOptionDescription(option)}
            </Text>
          )}
        </HStack>

        {/* Type label aligned right */}
        {context === 'menu' && option.type && (
          <Text
            size={{ mobile: 'xs', desktop: 'sm' }}
            color="blackAlpha.500"
            fontWeight="normal"
            flexShrink={0}
          >
            {getOptionTypeLabel(option)}
          </Text>
        )}
      </HStack>
    ),
    value,
    // @ts-ignore
    onChange: handleChange,
  };

  // FIXME: Two variations
  // "Overview": one tag inside then "+1" (use on mobile header and dashboard)
  // "Full": show all tags but below the input box, not inside

  switch (inputType) {
    case 'async':
    case 'async-creatable':
      return (
        <AsyncCreatableSelect<AutocompleteItem, true>
          {...selectProps}
          loadOptions={loadOptionsPromise}
        />
      );
    case 'creatable':
    default:
      return <Select<AutocompleteItem, true> {...selectProps} />;
  }
};
